/* eslint-disable consistent-return */
/* eslint-disable max-nested-callbacks */
import { Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import QualityChart, { RIGHT } from 'components/echart/QualityChart'
import Checkbox from 'components/forms/Checkbox'
import RadioButtons from 'components/forms/RadioButtons'
import WaitAction from 'wait/WaitAction'
import IAEauAction from 'iaeau/IAEauAction'
import { every, groupBy, isNil, isUndefined, max, maxBy, mean, minBy, orderBy, range, round, uniqBy, keys } from 'lodash'
import PropTypes from 'prop-types'
import { HYPE_TRENDS_CONSTANTS } from 'quality/constants/QualityConstants'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getRemarks, searchMinResult, searchMaxResult, calculateAverage, searchP90Result, filterValid, filterResult } from 'utils/AnalyseUtils'
import { formatHypeTrend, formatHypeTrendRupture, getTrendName } from 'utils/HypeUtils'
import { hasValue } from 'utils/NumberUtil'
import { getLabel } from 'utils/StoreUtils'
import { RemarkLegend, ThresholdLegend } from './Legend'
import ToastrAction from 'toastr/actions/ToastrAction'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import { AUTO, DEFAULT_NB_POINT_MOVING_AVERAGE, HYDRO, MONTH, PIEZO, PLUVIO, WEEK, YEAR } from 'quality/constants/ChartConstant'
import useBoolean from 'utils/customHook/useBoolean'
import useAbortController from 'utils/customHook/useAbortController'
import AnalysisAction from 'quality/actions/AnalysisAction'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { calculateThresholdsResult, findThreshold, getThresholdColors, THRESHOLD_COLOR_BY_SIZE, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import { NoDataCard } from 'components/card/NoDataCard'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE, SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'

const movingAveragePoints = [
    { value: 1, label: '3' },
    { value: 2, label: '5' },
    { value: 3, label: '7' },
    { value: 4, label: '9' },
]

const getStationName = (station, parameter) => {
    if (station?.code) {
        return `[${station?.code}] ${parameter?.displayName || ''} `
    }
    return `${station?.name || ''}, ${parameter?.displayName || ''}`
}

const ParameterGraph = ({
    analysis = [],
    additionalData = [],
    thresholds = [],

    hypeRuptures = [],
    hypeTrends = [],

    graphOptions = {},

    componentHeight = 600,

    withToolLegend = false,
    withArea = false,

    exportName,

    dataZoomPosition = { bottom: 40 },
    legendPosition = { bottom: 10, right: 20, top: undefined },
    toolboxPosition = { top: 5 },
}) => {
    const {
        qualitometers,
        parameters,
        units,
        settings,
        citiesIndex,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        settings: store.AdministrationReducer.settings,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const {
        regroupAxis = false,
        graphicTitle,

        displayStatistics = false,
        displayAverages = false,
        nbPointMovingAverage = DEFAULT_NB_POINT_MOVING_AVERAGE,
        displayHype = false,

        minY,
        maxY,
        xAxisSpace,
        maxXAxisSpace,
        showXSplitLines,
        showYSplitLines,

        stationsOptions = [],
    } = graphOptions

    const parametersCode = useMemo(() => uniqBy(analysis, 'parameter').map(a => a.parameter), [analysis])
    const onlyOneParameter = useMemo(() => parametersCode.length === 1, [parametersCode.length])
    const onlyOneQualitometer = useMemo(() => uniqBy(analysis, 'qualitometer').length === 1, [analysis])

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)
    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const unitsCode = useMemo(() => uniqBy(analysis, 'unit').map(a => a.unit || 'unknown'), [analysis])
    const unitByParam = useMemo(() => {
        const groupByParameter = groupBy(analysis, 'parameter')
        return keys(groupByParameter).reduce((acc, paramCode) => {
            const uniqUnit = uniqBy(groupByParameter[paramCode], 'unit').map(a => a.unit || 'unknown')
            acc[paramCode] = {
                unitsCode: uniqUnit.join('#'),
                unitslabel: uniqUnit.map(code => getLabel(units, code, showSandreCode ? 'symbolWithCode' : 'symbol') || i18n.unknownUnit).join(', '),
            }
            return acc
        }, {})
    }, [analysis, units, showSandreCode])

    const paramThreshold = useMemo(() => {
        const fractions = uniqBy(analysis, 'fraction').map(a => a.fraction)
        if (!onlyOneParameter || ((fractions.length > 1 || unitsCode.length > 1) && !regroupAxis)) {
            return
        }
        const [parameterCode] = parametersCode
        const unitCode = unitsCode.length === 1 ? analysis[0]?.unit : undefined
        const qualitometerId = onlyOneQualitometer ? analysis[0]?.qualitometer : undefined
        return findThreshold(thresholds, { parameter: parameterCode, unit: unitCode, qualitometer: qualitometerId, fraction: fractions[0] })
    }, [analysis, onlyOneParameter, onlyOneQualitometer, parametersCode, regroupAxis, thresholds, unitsCode.length])

    const getMarkerColor = (analyse) => {
        if (!isNil(analyse.remark) && analyse.remark !== '1') {
            return 'black'
        }
        if (!onlyOneParameter || isUndefined(paramThreshold)) {
            return undefined
        }
        return isNil(analyse.index) ? 'black' : analyse.color
    }

    const analysisFormatted = useMemo(() => {
        const groupAnalysis = groupBy(analysis, a => regroupAxis ? `${a.qualitometer}#${a.parameter}` : `${a.qualitometer}#${a.parameter}#${a.unit || 'unknown'}`)
        return keys(groupAnalysis).flatMap(key => {
            const [stationId, paramCode, unitCode] = key.split('#')
            const parseStationId = parseInt(stationId)
            const stationOptions = stationsOptions.find(o => o.id === parseStationId)
            const {
                [key]: analysisFiltered = [],
            } = groupAnalysis
            const parameter = parameters.find(p => p.code === paramCode)
            const qualitometer = qualitometers.find(q => q.id === parseStationId)
            const city = citiesIndex[qualitometer?.townCode]

            return {
                // uniqId: parseStationId,
                name: getStationName(qualitometer, parameter),
                unit: unitByParam[paramCode]?.unitslabel,
                yAxisId: regroupAxis ? unitByParam[paramCode]?.unitsCode : unitCode,
                color: stationOptions?.color || (onlyOneQualitometer && onlyOneParameter ? 'black' : undefined),
                dataList: analysisFiltered.map(a => ({
                    date: a.sampleDate,
                    value: a.result,
                    displayValue: a.value ?? a.result,
                    unit: getLabel(units, a.unit, 'symbol') || i18n.unknownUnit,
                    remark: a.remark,
                    marker: getRemarks(a.remark)?.plot,
                    color: getMarkerColor(a),
                })),
                city: showInseeCode ? city?.labelWithCode : city?.name,
                label: qualitometer.name,
            }
        })
    }, [analysis, onlyOneParameter, onlyOneQualitometer, parameters, qualitometers, regroupAxis, stationsOptions, units, unitByParam, showInseeCode, citiesIndex])

    const additionalDataFormated = useMemo(() => {
        return additionalData.map(d => ({
            name: d.name,
            unit: d.unit,
            yAxisId: `${d.dataType}_${d.unit}`,
            type: d.type,
            lineStyle: {
                type: d.lineStyle,
            },
            showSymbol: d.showSymbol,
            connectNulls: d.connectNulls,
            dataType: d.dataType,
            dataList: d.dataList,
            excludeFromInterval: true,
        }))
    }, [additionalData])

    const parametersObj = parametersCode.map(code => parameters.find(p => p.code === code)).filter(u => !!u)
    const unitsObj = uniqBy(analysis, 'unit').map(a => units.find(u => u.code === a.unit)).filter(u => !!u)

    const chartTitle = isUndefined(graphicTitle) ? `${parametersObj.map(p => showSandreCode ? p.displayLabel : p.displayName).join(', ')} ${unitsObj.length ? `(${unitsObj.map(u => showSandreCode ? u.symbolWithCode : u.symbol).join(', ')})` : ''}` : graphicTitle

    const thresholdFormated = useMemo(() => {
        if (!paramThreshold) {
            return []
        }
        const [paramCode] = parametersCode
        const [unitCode] = unitsCode

        if (isUndefined(paramThreshold)) return []

        const colorList = getThresholdColors(paramThreshold)

        if (paramThreshold.thresholdType === THRESHOLD_TYPE.BOUNDED) {
            return [{
                yAxisId: regroupAxis ? unitByParam[paramCode]?.unitsCode : unitCode,
                colorList,
                dataList: [
                    {
                        value: paramThreshold.threshold1,
                        color: 'red',
                    },
                    {
                        value: paramThreshold.threshold2,
                        color: 'red',
                    },
                ],
            }]
        }

        return [{
            yAxisId: regroupAxis ? unitByParam[paramCode]?.unitsCode : unitCode,
            colorList,
            dataList: [1, 2, 3, 4].map(nb => ({
                value: paramThreshold[`threshold${nb}`],
                color: colorList[nb],
            })).filter(t => !isUndefined(t.value)),
        }]
    }, [paramThreshold, parametersCode, regroupAxis, unitByParam, unitsCode])

    const markLines = useMemo(() => {
        if (!displayStatistics) {
            return []
        }
        const validAnalysis = filterValid(filterResult(analysis))
        if (!validAnalysis.length) {
            return []
        }
        const minResult = searchMinResult(validAnalysis)
        const maxResult = searchMaxResult(validAnalysis)
        const p90Result = searchP90Result(validAnalysis)
        const averageResult = calculateAverage(validAnalysis, settings)

        const [paramCode] = parametersCode
        const [unitCode] = unitsCode

        return [{
            yAxisId: regroupAxis ? unitByParam[paramCode]?.unitsCode : unitCode,
            dataList: [
                {
                    value: minResult,
                    label: i18n.min,
                },
                {
                    value: maxResult,
                    label: i18n.max,
                },
                {
                    value: p90Result,
                    label: i18n.percentile90,
                },
                {
                    value: averageResult,
                    label: i18n.average,
                },
            ],
        }]
    }, [analysis, displayStatistics, parametersCode, regroupAxis, settings, unitByParam, unitsCode])

    const movingAverage = useMemo(() => {
        if (!displayAverages || parametersCode.length !== 1) {
            return []
        }
        const [paramCode] = parametersCode
        const analysisGroup = groupBy(analysis, a => regroupAxis ? a.qualitometer : `${a.qualitometer}#${a.unit || 'unknown'}`)
        return keys(analysisGroup).map(key => {
            const [, unit] = key.split('#')
            const orderedAnalysis = orderBy(analysisGroup[key], 'sampleDate')
            const dataList = orderedAnalysis.flatMap((a, index) => {
                if (index < nbPointMovingAverage || index >= (orderedAnalysis.length - nbPointMovingAverage)) {
                    return []
                }
                const rangeList = range(index - nbPointMovingAverage, index + nbPointMovingAverage + 1)
                const average = mean(rangeList.map(idx => orderedAnalysis[idx].result))
                return { date: a.sampleDate, value: round(average, 3) }
            })
            const { qualitometer: stationId } = analysisGroup[key][0]
            const qualitometer = qualitometers.find(q => q.id === stationId)
            const stationOptions = stationsOptions.find(o => o.id === stationId)
            return {
                name: `[${qualitometer?.code || key}] ${i18n.movingAverage} ${nbPointMovingAverage * 2 + 1}`,
                lineStyle: {
                    type: 'dotted',
                },
                color: stationOptions?.color,
                yAxisId: regroupAxis ? unitByParam[paramCode]?.unitsCode : unit,
                dataList,
            }
        })
    }, [analysis, displayAverages, nbPointMovingAverage, parametersCode, qualitometers, regroupAxis, stationsOptions, unitByParam])

    const hypeLines = useMemo(() => {
        const ids = uniqBy(analysis, 'qualitometer').map(a => a.qualitometer)
        if (!displayHype || ids.length !== 1 || parametersCode.length !== 1) {
            return []
        }

        if (hypeTrends.length || hypeRuptures.length) {
            const [paramCode] = parametersCode
            const hypeData = analysis.filter(a => hasValue(a.value)).map(a => ({
                date: a.sampleDate,
                result: a.result || 0,
            }))
            const maxValue = max([maxBy(hypeData, 'result')?.result || 0, ...thresholdFormated.map(t => t.value)])
            const minDate = minBy(hypeData, 'date')?.date
            const maxDate = maxBy(hypeData, 'date')?.date
            const lines = hypeTrends.filter(t => t.coefficient && t.value).map(trend => {
                return {
                    name: getTrendName(trend.statisticsType),
                    showSymbol: false,
                    yAxisId: unitByParam[paramCode]?.unitsCode,
                    dataList: formatHypeTrend(trend.coefficient, trend.value, minDate, maxDate),
                }
            })
            const ruptures = hypeRuptures.flatMap(rupture => {
                const name = getTrendName(rupture.statisticsType)
                const ruptureLine = {
                    name,
                    dataList: [
                        { date: rupture.breakDate, value: 1.1 * maxValue },
                        { date: rupture.breakDate, value: 0 },
                    ],
                    yAxisId: unitByParam[paramCode]?.unitsCode,
                    lineStyle: {
                        type: 'dashed',
                    },
                }
                if (rupture.statisticsType === HYPE_TRENDS_CONSTANTS.AVERAGE_RUPTURE && every(['preValue', 'postValue', 'breakDate'], key => hasValue(rupture[key]))) {
                    // average line
                    const line = {
                        name,
                        connectNulls: false,
                        yAxisId: unitByParam[paramCode]?.unitsCode,
                        dataList: [
                            { date: minDate, value: rupture.preValue },
                            { date: rupture.breakDate, value: rupture.preValue },
                            { date: rupture.breakDate, value: null },
                            { date: rupture.breakDate, value: rupture.postValue },
                            { date: maxDate, value: rupture.postValue },
                        ],
                    }
                    return [ruptureLine, line]
                } else if (rupture.statisticsType === HYPE_TRENDS_CONSTANTS.TREND_RUPTURE && (['preValue', 'preCoefficient', 'breakDate'].every(k => hasValue(rupture[k])) || ['postValue', 'postCoefficient', 'breakDate'].every(k => hasValue(rupture[k])))) {
                    // trend inversion line
                    const line = {
                        name,
                        connectNulls: false,
                        showSymbol: false,
                        yAxisId: unitByParam[paramCode]?.unitsCode,
                        dataList: formatHypeTrendRupture(rupture, minDate, maxDate),
                    }
                    return [ruptureLine, line]
                }
                return []
            })
            return [...lines, ...ruptures]
        }
        return []
    }, [analysis, displayHype, hypeRuptures, hypeTrends, parametersCode, thresholdFormated, unitByParam])

    const yAxis = useMemo(() => {
        const additionalYAxis = uniqBy(additionalData, d => `${d.dataType}_${d.unit}`).map(d => ({
            name: d.unit,
            yAxisId: `${d.dataType}_${d.unit}`,
            position: RIGHT,
            inverse: d.inverse,
            // min: minY,
            // max: maxY,
            dataType: d.dataType,
            splitLine: { show: showYSplitLines },
        }))
        if (regroupAxis) {
            const paramAxis = parametersCode.map(code => {
                return {
                    name: unitByParam[code]?.unitslabel,
                    yAxisId: unitByParam[code]?.unitsCode,
                    min: minY,
                    max: maxY,
                    splitLine: { show: showYSplitLines },
                }
            })
            return [
                ...uniqBy(paramAxis, 'yAxisId'),
                ...additionalYAxis,
            ]
        }
        const unitAxis = unitsCode.map(code => ({
            name: getLabel(units, code, showSandreCode ? 'symbolWithCode' : 'symbol') || i18n.unknownUnit,
            yAxisId: code,
            min: minY,
            max: maxY,
            splitLine: { show: showYSplitLines },
        }))
        return [
            ...unitAxis,
            ...additionalYAxis,
        ]
    }, [additionalData, maxY, minY, parametersCode, regroupAxis, showYSplitLines, unitByParam, units, unitsCode, showSandreCode])

    return (
        <QualityChart
            title={chartTitle}
            exportName={exportName ?? `${i18n.follow} - ${parametersObj.map(p => p.displayName).join(', ')}`}
            grids={[{
                height: 'auto',
                bottom: 120,
                top: 10,
            }]}
            xAxis={[{
                splitLine: { show: showXSplitLines },
            }]}
            yAxis={yAxis}
            series={[...additionalDataFormated, ...analysisFormatted, ...movingAverage, ...hypeLines]}
            thresholds={thresholdFormated}
            markLines={markLines}

            headerHeight={0}
            footerHeight={120}
            componentHeight={componentHeight}

            xAxisSpace={xAxisSpace}
            maxXAxisSpace={maxXAxisSpace}

            withToolTypeLine
            withToolTypeBar={!displayHype && additionalData.length === 0}
            withToolTypeStack={!displayHype && additionalData.length === 0}
            withToolLog={!displayHype && additionalData.length === 0}
            withToolQuantification
            withToolThreshold
            withToolLegend={withToolLegend}
            withToolMarker
            withToolLine={!displayHype}

            withArea={withArea}
            withDataZoom
            withHidingYAxis

            dataZoomPosition={dataZoomPosition}
            legendPosition={legendPosition}
            toolboxPosition={toolboxPosition}
        />
    )
}

ParameterGraph.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({/* ...DtoAnalysis, ...calculateThresholdsResult */ })),
    additionalData: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        unit: PropTypes.string,
        dataType: PropTypes.oneOf([PLUVIO, PIEZO, HYDRO]),
        dataList: PropTypes.arrayOf(PropTypes.shape({
            date: PropTypes.number,
            value: PropTypes.number,
        })),
        showSymbol: PropTypes.bool,
        inverse: PropTypes.bool,
        type: PropTypes.string,
    })),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),

    hypeRuptures: PropTypes.arrayOf(PropTypes.shape({})),
    hypeTrends: PropTypes.arrayOf(PropTypes.shape({})),

    graphOptions: PropTypes.shape({
        regroupAxis: PropTypes.bool,
        graphicTitle: PropTypes.string,

        displayStatistics: PropTypes.bool,
        displayAverages: PropTypes.bool,
        nbPointMovingAverage: PropTypes.number,
        displayHype: PropTypes.bool,

        minY: PropTypes.number,
        maxY: PropTypes.number,
        xAxisSpace: PropTypes.oneOf([YEAR, MONTH, WEEK, AUTO]),
        maxXAxisSpace: PropTypes.oneOf([YEAR, MONTH, WEEK, AUTO]),
        showXSplitLines: PropTypes.bool,
        showYSplitLines: PropTypes.bool,

        stationsOptions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            color: PropTypes.string,
        })),
    }),

    graphHeight: PropTypes.number,
    componentHeight: PropTypes.number,

    withToolLegend: PropTypes.bool,
    withArea: PropTypes.bool,

    exportName: PropTypes.string,

    dataZoomPosition: PropTypes.shape({}),
    legendPosition: PropTypes.shape({}),
    gridPosition: PropTypes.shape({}),
    toolboxPosition: PropTypes.shape({}),
}

const ParameterGraphModal = ({
    isOpen = false,
    closeGraph = () => { },

    analysis = [],
    thresholds = [],
    additionalData = [],
    parameter,

    graphOptions = {},
    hideActions = false,
    dialogTitle = 'default',
}) => {
    const dispatch = useDispatch()
    const {
        qualitometers,
        parameters,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const [displayStatistics, setDisplayStatistics] = useState(false)
    const [displayAverages, setDisplayAverages] = useState(false)
    const [nbPointMovingAverage, setNbPointMovingAverage] = useState(1)
    const [displayHype, setDisplayHype] = useState(false)

    const [hypeRuptures, setHypeRuptures] = useState([])
    const [hypeTrends, setHypeTrends] = useState([])

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    useEffect(() => {
        if (!isOpen) {
            setDisplayStatistics(false)
            setDisplayAverages(false)
            setNbPointMovingAverage(1)
            setDisplayHype(false)
        }
    }, [isOpen])

    useEffect(() => {
        const ids = uniqBy(analysis, 'qualitometer').map(a => a.qualitometer)
        if (displayHype && ids.length === 1) {
            const [id] = ids
            const qualitometer = qualitometers.find(q => q.id === id)
            if (!qualitometer) {
                return
            }

            const hypeData = analysis.filter(a => hasValue(a.value)).map((a, index) => ({
                stationCode: qualitometer.code,
                stationTypeCode: qualitometer.stationType,
                parameterCode: parseInt(a.parameter) || 0,
                analysisNumber: index,
                measureDate: a.sampleDate,
                measure: a.result,
                unitCode: 0,
                remark: 1,
            }))
            if (hypeData.length) {
                dispatch(WaitAction.waitStart())
                dispatch(IAEauAction.getHypeTrend(hypeData, qualitometer.code))
                    .then(({ trends, breaks }) => {
                        setHypeTrends(trends)
                        setHypeRuptures(breaks)
                        if (!trends.length && !breaks.length) {
                            dispatch(ToastrAction.warning(i18n.hypeNoTrendsDetected))
                            setDisplayHype(false)
                        }
                    })
                    .finally(() => {
                        dispatch(WaitAction.waitStop())
                    })
            }
        }
    }, [analysis, dispatch, displayHype, qualitometers])

    const onlyOneQualitometer = useMemo(() => uniqBy(analysis, 'qualitometer').length === 1, [analysis])
    const onlyOneUnit = useMemo(() => uniqBy(analysis, 'unit').length === 1, [analysis])

    const parameterCode = analysis[0]?.parameter || parameter

    const parameterObj = parameters.find(p => p.code === parameterCode)
    const dialogUsedTitle = dialogTitle !== 'default' ? dialogTitle : `${i18n.followUp} ${parameterObj?.name || ''} ${(parameterCode && showSandreCode) ? `[${parameterCode}]` : ''}`

    const paramThreshold = useMemo(() => {
        const unitCode = onlyOneUnit ? analysis[0]?.unit : undefined
        const qualitometerId = onlyOneQualitometer ? analysis[0]?.qualitometer : undefined
        const fractions = uniqBy(analysis, 'fraction').map(a => a.fraction)
        const fractionCode = fractions.length > 1 ? undefined : fractions[0]
        return findThreshold(thresholds, { parameter: parameterCode, unit: unitCode, qualitometer: qualitometerId, fraction: fractionCode })
    }, [analysis, onlyOneQualitometer, onlyOneUnit, parameterCode, thresholds])

    const colorTab = useMemo(() => {
        if (!paramThreshold) {
            return {}
        }
        if (paramThreshold.thresholdType === THRESHOLD_TYPE.BOUNDED) {
            return ['red', 'blue', 'red']
        }
        const nbThresholds = [1, 2, 3, 4].filter(nb => hasValue(paramThreshold[`threshold${nb}`])).length + 1
        return THRESHOLD_COLOR_BY_SIZE[nbThresholds]
    }, [paramThreshold])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
        >
            <DefaultDialogTitle
                title={dialogUsedTitle}
                onClose={closeGraph}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                { analysis.length && (
                    <Grid container columnSpacing={2}>
                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <ParameterGraph
                                        analysis={analysis}
                                        additionalData={additionalData}
                                        thresholds={thresholds}

                                        hypeRuptures={hypeRuptures}
                                        hypeTrends={hypeTrends}

                                        componentHeight={560}
                                        withArea

                                        graphOptions={{
                                            ...graphOptions,
                                            displayStatistics,
                                            displayAverages,
                                            nbPointMovingAverage,
                                            displayHype,
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={colorTab.length ? 8 : 12}>
                            <RemarkLegend
                                list={analysis}
                                col={colorTab.length ? 4 : 3}
                                iconColor='black'
                            />
                        </Grid>
                        {
                            !!colorTab.length && (
                                <Grid size={4}>
                                    <ThresholdLegend
                                        threshold={paramThreshold}
                                    />
                                </Grid>
                            )
                        }
                    </Grid>) || <NoDataCard />}
            </DialogContent>
            {
                (!hideActions && !!analysis.length) && (
                    <DialogActions>
                        <Grid container alignItems='center' spacing={2} sx={{ width: '100%' }}>
                            <Grid size={3}>
                                <Checkbox
                                    checked={displayStatistics}
                                    label={i18n.displayStatistics}
                                    onChange={setDisplayStatistics}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Checkbox
                                    checked={displayAverages}
                                    label={i18n.displayAverages}
                                    onChange={setDisplayAverages}
                                />
                            </Grid>
                            <Grid size={3}>
                                <RadioButtons
                                    selected={nbPointMovingAverage}
                                    elements={movingAveragePoints}
                                    title={i18n.numberOfPoints}
                                    onChange={setNbPointMovingAverage}
                                    disabled={!displayAverages}
                                />
                            </Grid>
                            {
                                onlyOneQualitometer && (
                                    <Grid size={3}>
                                        <Checkbox
                                            checked={displayHype}
                                            label={i18n.displayTrends}
                                            onChange={setDisplayHype}
                                        />
                                    </Grid>
                                )
                            }
                        </Grid>
                    </DialogActions>
                )
            }
        </Dialog>
    )
}

ParameterGraphModal.propTypes = {
    isOpen: PropTypes.bool,
    closeGraph: PropTypes.func,
    analysis: PropTypes.arrayOf(PropTypes.shape({/* ...DtoAnalysis, ...calculateThresholdsResult */ })),
    additionalData: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        unit: PropTypes.string,
        dataType: PropTypes.oneOf([PLUVIO, PIEZO, HYDRO]),
        dataList: PropTypes.arrayOf(PropTypes.shape({
            date: PropTypes.number,
            value: PropTypes.number,
        })),
        showSymbol: PropTypes.bool,
        inverse: PropTypes.bool,
        type: PropTypes.string,
    })),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    parameter: PropTypes.string,

    graphOptions: PropTypes.shape({
        graphicTitle: PropTypes.string,
        regroupAxis: PropTypes.bool,
        displayStatistics: PropTypes.bool,
        displayAverages: PropTypes.bool,
        nbPointMovingAverage: PropTypes.number,
        displayHype: PropTypes.bool,
    }),
    hideActions: PropTypes.bool,
    dialogTitle: PropTypes.string,
}

const SuperParameterGraphModal = ({
    isOpen = false,
    closeGraph = () => { },

    parameter,
    qualitometer,
    thresholds = [],
    additionalData = [],

    graphOptions = {},
}) => {
    const dispatch = useDispatch()

    const {
        controllerRef,
        initController,
    } = useAbortController()

    const defaultDraphOptions = useMemo(() => ({
        regroupAxis: true,
        ...graphOptions,
    }), [graphOptions])

    const [analysis, setAnalysis] = useState([])
    const {
        value: isLoaded,
        setTrue: loaded,
        setFalse: notLoaded,
    } = useBoolean(false)

    useEffect(() => {
        if (isUndefined(parameter) || isUndefined(qualitometer) || !isOpen) {
            return
        }

        notLoaded()
        setAnalysis([])
        initController()

        const analysisFilter = {
            lightMode: true, // Option[Boolean] = None,

            parameters: [parameter], // Option[Seq[String]] = None,
            stations: [qualitometer], // Option[Seq[Int]] = None
        }

        AnalysisAction.getAnalysis(analysisFilter, controllerRef.current.signal)
            .then(list => setAnalysis(list.map(a => new DtoAnalysisLight(a))))
            .finally(loaded)

        return () => {
            controllerRef.current.abort()
        }
    }, [dispatch, parameter, qualitometer, isOpen])

    const formattedAnalysis = useMemo(() => {
        return analysis.map(a => ({
            ...a,
            ...calculateThresholdsResult(a, thresholds),
        }))
    }, [analysis, thresholds])

    return (
        <ParameterGraphModal
            isOpen={isOpen && isLoaded}
            closeGraph={closeGraph}

            analysis={formattedAnalysis}
            thresholds={thresholds}
            additionalData={additionalData}
            parameter={parameter}

            graphOptions={{ ...defaultDraphOptions }}
        />
    )
}

SuperParameterGraphModal.propTypes = {
    isOpen: PropTypes.bool,
    closeGraph: PropTypes.func,

    parameter: PropTypes.string,
    qualitometer: PropTypes.number,
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    additionalData: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        unit: PropTypes.string,
        dataType: PropTypes.oneOf([PLUVIO, PIEZO, HYDRO]),
        dataList: PropTypes.arrayOf(PropTypes.shape({
            date: PropTypes.number,
            value: PropTypes.number,
        })),
        showSymbol: PropTypes.bool,
        inverse: PropTypes.bool,
        type: PropTypes.string,
    })),

    graphOptions: PropTypes.shape({
        graphicTitle: PropTypes.string,
        regroupAxis: PropTypes.bool,
        displayStatistics: PropTypes.bool,
        displayAverages: PropTypes.bool,
        nbPointMovingAverage: PropTypes.number,
        displayHype: PropTypes.bool,
    }),
}

const LargerPropertiesParameterGraphModal = ({
    isOpen = false,
    closeGraph = () => { },

    parameters,
    qualitometers,
    thresholds = [],
    startPeriod,
    endPeriod,
    quantificationControl = false,
    excludeProducers = [],
    dialogTitle,
    additionalData = [],
    support = '3',

    graphOptions = {},
}) => {
    const dispatch = useDispatch()

    const {
        controllerRef,
        initController,
    } = useAbortController()

    const defaultGraphOptions = useMemo(() => ({
        regroupAxis: true,
        ...graphOptions,
    }), [graphOptions])

    const [analysis, setAnalysis] = useState([])
    const {
        value: isLoaded,
        setTrue: loaded,
        setFalse: notLoaded,
    } = useBoolean(false)

    useEffect(() => {
        if (isUndefined(parameters) || !parameters?.length || parameters[0] === '-1' || isUndefined(qualitometers) || !qualitometers?.length || !isOpen) {
            setAnalysis([])
            return
        }

        notLoaded()
        setAnalysis([])
        initController()

        const analysisFilter = {
            lightMode: true, // Option[Boolean] = None,
            startDate: startPeriod,
            endDate: endPeriod,
            parameters, // Option[Seq[String]] = None,
            stations: qualitometers, // Option[Seq[Int]] = None
            quantificationControl,
            excludedProducers: excludeProducers,
            support,
        }

        AnalysisAction.getAnalysis(analysisFilter, controllerRef.current.signal)
            .then(list => setAnalysis(list?.map(a => new DtoAnalysisLight(a))))
            .finally(loaded)

        return () => {
            controllerRef.current.abort()
        }
    }, [dispatch, parameters, qualitometers, isOpen, startPeriod, endPeriod])

    const formattedAnalysis = useMemo(() => {
        return analysis?.map(a => ({
            ...a,
            ...calculateThresholdsResult(a, thresholds),
        }))
    }, [analysis, thresholds])

    return (
        <ParameterGraphModal
            isOpen={isOpen && isLoaded}
            closeGraph={closeGraph}

            analysis={formattedAnalysis}
            thresholds={thresholds}
            additionalData={additionalData}
            parameter={parameters?.length && parameters[0]}
            dialogTitle={dialogTitle}

            graphOptions={{ ...defaultGraphOptions }}
        />
    )
}

LargerPropertiesParameterGraphModal.propTypes = {
    isOpen: PropTypes.bool,
    closeGraph: PropTypes.func,

    parameters: PropTypes.arrayOf(PropTypes.string),
    qualitometers: PropTypes.arrayOf(PropTypes.number),
    thresholds: PropTypes.arrayOf(PropTypes.shape(QualityThresholdType)),
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
    quantificationControl: PropTypes.bool,
    excludeProducers: PropTypes.bool,
    dialogTitle: PropTypes.string,
    support: PropTypes.string,
    additionalData: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        unit: PropTypes.string,
        dataType: PropTypes.oneOf([PLUVIO, PIEZO, HYDRO]),
        dataList: PropTypes.arrayOf(PropTypes.shape({
            date: PropTypes.number,
            value: PropTypes.number,
        })),
        showSymbol: PropTypes.bool,
        inverse: PropTypes.bool,
        type: PropTypes.string,
    })),

    graphOptions: PropTypes.shape({
        graphicTitle: PropTypes.string,
        regroupAxis: PropTypes.bool,
        displayStatistics: PropTypes.bool,
        displayAverages: PropTypes.bool,
        nbPointMovingAverage: PropTypes.number,
        displayHype: PropTypes.bool,
    }),
}

export {
    ParameterGraphModal,
    SuperParameterGraphModal,
    LargerPropertiesParameterGraphModal,
    ParameterGraph,
}